/**
 * Releases a pinned slideshow hero once the scroll story overlay has finished
 * revealing (overlay top reaches viewport top) or the overlay section has
 * fully scrolled past (overlay bottom leaves viewport).
 */
class SlideshowScrollPin extends HTMLElement {
  /** @type {AbortController | null} */
  #abort = null;
  /** @type {number | null} */
  #rafId = null;

  connectedCallback() {
    const section = this.closest('.shopify-section');
    const hero = section?.querySelector('.slideshow-section--scroll-pinned');
    const overlay = this.#findOverlay(section);

    if (!hero || !overlay) return;

    this.#abort = new AbortController();
    const { signal } = this.#abort;

    const update = () => {
      this.#rafId = null;
      const { top, bottom } = overlay.getBoundingClientRect();
      const released = top <= 0 || bottom <= 0;
      hero.classList.toggle('slideshow-section--scroll-released', released);
    };

    const scheduleUpdate = () => {
      if (this.#rafId != null) return;
      this.#rafId = requestAnimationFrame(update);
    };

    window.addEventListener('scroll', scheduleUpdate, { passive: true, signal });
    window.addEventListener('resize', scheduleUpdate, { passive: true, signal });
    scheduleUpdate();
  }

  disconnectedCallback() {
    this.#abort?.abort();
    if (this.#rafId != null) {
      cancelAnimationFrame(this.#rafId);
      this.#rafId = null;
    }
  }

  /**
   * @param {Element | null | undefined} slideshowSection
   * @returns {Element | null}
   */
  #findOverlay(slideshowSection) {
    let next = slideshowSection?.nextElementSibling ?? null;

    while (next) {
      if (next.classList.contains('wbs-scroll-story-overlay-wrapper')) return next;
      if (next.classList.contains('shopify-section')) break;
      next = next.nextElementSibling;
    }

    return null;
  }
}

if (!customElements.get('slideshow-scroll-pin')) {
  customElements.define('slideshow-scroll-pin', SlideshowScrollPin);
}
